import { combineLatest, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { queryHashGenerator } from '../../../utils/queryHash';
import {
  EntityType,
  IComputedAggregate,
  ICrossTestMetricSelection,
  IMetric,
  ISelectableEntity,
  ITestType,
} from '../../types';
import {
  crossTestAggregates,
  ICrossTestAggregates,
} from '../crossTestAggregatesProvider';
import {
  ITrendReportSettings,
  ITrendReportSettingsState,
  trendReportSettingsProvider,
} from '../settings/reports/trendReportSettingsProvider';
import { ISettingsTemplate } from '../settings/reports/types';
import {
  formatTimeframe,
  formatTimeframeFromReportResults,
} from '../utils/formatTimeframe';
import { gatherQueryHashes } from '../utils/gatherQueryHashes';
import { getSessionRangeTimestamps } from '../utils/getSessionRangeTimestamps';
import { isValidDateRange } from '../utils/isValidDateRange';
import { IReportResult, IReportState } from './types';

export interface ITrendResult extends IReportResult {
  metric: IMetric;
  testType: ITestType;
  tags: string[];
  combined: IComputedAggregate;
  sessions: IComputedAggregate[];
  timeframe: string;
  isValid: boolean;
  whiskers: boolean;
}

export interface ITrendReportState extends IReportState {
  settings: ISettingsTemplate<ITrendReportSettings>;
  data: ITrendResult[];
  timeframe: string;
  whiskers: boolean;
}

/**
 * @description pipeline that observes the aggregates requested based on the trend report settings
 * and then determines and emits the state of the report
 * @param entityType
 * @param entityId
 * @returns
 */
export const trendReportProvider = (
  entityType: EntityType,
  entityId: string
): Observable<ITrendReportState> => {
  return trendReportSettingsProvider().pipe(
    /// observe the aggregates as defined in the settings
    switchMap(
      (
        settingsState: ITrendReportSettingsState
      ): Observable<[ITrendReportSettingsState, ICrossTestAggregates]> => {
        if (settingsState?.selected?.settings && settingsState?.timeframe) {
          const { metrics } = settingsState.selected.settings;
          const { timeframe } = settingsState;
          /// gather the list of unique query hashes
          const queryHashes = gatherQueryHashes(metrics);
          return combineLatest([
            of<ITrendReportSettingsState>(settingsState),
            crossTestAggregates(
              queryHashes,
              entityId,
              entityType,
              timeframe.dateType,
              timeframe.dateRange
            ),
          ]);
        } else {
          return of([settingsState, {}]);
        }
      }
    ),
    /// map the aggregates to a state
    map(
      (
        result: [ITrendReportSettingsState, ICrossTestAggregates]
      ): ITrendReportState => {
        const [settings, aggregates] = result;

        const settingsValidation = validateSettings(settings);
        if (settingsValidation.isValid) {
          const trendResults = makeTrendResults(
            settings?.selected?.settings,
            aggregates
          );
          const dataValidation = validateData(trendResults);
          return {
            ...dataValidation,
            settings: settings.selected,
            data: trendResults,
            timeframe: formatTimeframeFromReportResults(trendResults),
            whiskers: settings.whiskers,
          };
        } else {
          /// settings are invalid
          return {
            ...settingsValidation,
            settings: settings.selected,
            data: [],
            timeframe: null,
            whiskers: null,
          };
        }
      }
    )
  );
};

/**
 * @description generates the individual trend result for each metric defined in the report settings
 * @param settings
 * @param aggregates
 * @returns
 */
const makeTrendResults = (
  settings: ITrendReportSettings,
  aggregates: ICrossTestAggregates
): ITrendResult[] => {
  const whiskers = settings.whiskers;
  return settings?.metrics?.map(
    (selection: ICrossTestMetricSelection): ITrendResult => {
      const metric = selection.metric.entity;
      const testType = selection.testType.entity;
      const tags =
        selection.tags?.map((se: ISelectableEntity): string => se.label) ?? [];
      const queryHash = queryHashGenerator(
        selection.testType.entity.id,
        selection.tags?.map((se: ISelectableEntity): string => se.value)
      );

      const sessions = aggregates?.[queryHash]?.sessions;
      const combined = aggregates?.[queryHash]?.combined;
      const { start, end } = getSessionRangeTimestamps(sessions);
      const isValid = sessions?.reduce(
        (hasDataForMetric: boolean, session: IComputedAggregate): boolean => {
          if (hasDataForMetric) return hasDataForMetric;
          return session.data[metric.id]?.count > 0;
        },
        false
      );
      return {
        metric,
        sessions,
        combined,
        testType,
        tags,
        timestamp: start,
        end,
        timeframe: formatTimeframe(start, end),
        isValid,
        whiskers,
      };
    }
  );
};

interface IValidationResult {
  isValid: boolean;
  errorCode?: string;
}

/**
 * @description validates the user settings for the report
 * @param settings
 * @returns
 */
const validateSettings = (
  settings: ITrendReportSettingsState
): IValidationResult => {
  if (!settings?.selected) {
    return {
      isValid: false,
      errorCode: 'missing-template',
    };
  }
  /// make sure a least one metric is defined
  else if (
    !settings?.selected?.settings?.metrics ||
    settings?.selected?.settings?.metrics?.length === 0
  ) {
    return {
      isValid: false,
      errorCode: 'missing-metrics',
    };
    /// make sure the date range is valid
  } else if (!isValidDateRange(settings?.timeframe)) {
    return {
      isValid: false,
      errorCode: 'missing-date-range',
    };
  } else {
    return {
      isValid: true,
    };
  }
};

/**
 * @description ensures that at least one trend result has data
 * @param results
 */

const validateData = (results: ITrendResult[]): IValidationResult => {
  const isValid = results.reduce(
    (acc: boolean, curr: ITrendResult): boolean => {
      if (acc) return acc;
      return curr.isValid;
    },
    false
  );
  if (isValid) {
    return {
      isValid: true,
      errorCode: null,
    };
  } else {
    return {
      isValid: false,
      errorCode: 'insufficient-data',
    };
  }
};
